import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { IconTile } from '@atlaskit/icon';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import Avatar from '@atlaskit/avatar';

import { TeamProfileCardWithTrigger } from '@atlassian/team-profilecard/trigger';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { GroupOrTeamPrincipal } from '../../../../commonTypes.tsx';

const i18n = defineMessages({
	groupText: {
		id: 'share-and-restrict-dialog.principal-details.group-text',
		defaultMessage: 'Group',
		description: 'Label indicating that an entity in a permissions table is a group',
	},
	adminGroupText: {
		id: 'share-and-restrict-dialog.principal-details.admin-group-text',
		defaultMessage: 'Admin group',
		description: 'Label indicating that an entity in a permissions table is a admin group',
	},
	teamText: {
		id: 'share-and-restrict-dialog.principal-details.team-text',
		defaultMessage: 'Team',
		description: 'Label indicating that an entity in a permissions table is a team',
	},
	officialTeamText: {
		id: 'share-and-restrict-dialog.principal-details.official-team-text',
		defaultMessage: 'Official team',
		description: 'Label indicating that an entity in a permissions table is a team',
	},
	membersCount: {
		id: 'share-and-restrict-dialog.principal-details.members-count.one-person',
		defaultMessage: '{count, plural, one { •\u00A01 member} other { •\u00A0{count} members}}',
		description: 'user count subtext for groups and teams',
	},
	verifiedTeamToolTip: {
		id: 'share-and-restrict-dialog.principal-details.verified-team-tool-tip',
		defaultMessage: 'This team is verified because it is controlled by an organization admin.',
		description: 'A tooltip explaining the verified icon — what it means and why it’s displayed.',
	},
});

const principalDetailsStyle = xcss({
	overflowWrap: 'anywhere',
});

const iconContainer = xcss({
	borderRadius: 'radius.full',
	width: '36px',
	height: '36px',
	backgroundColor: 'color.background.neutral',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexShrink: 0,
	marginTop: 'space.0',
});

export const GroupDetails: FC<GroupOrTeamPrincipal> = ({
	id,
	name,
	usageType,
	type,
	memberCount,
	isVerified,
	avatarSrc,
	teamTypeName,
}) => {
	const { formatMessage } = useIntl();
	const { cloudId } = useSessionData();

	const isTeamType = usageType === 'TEAM_COLLABORATION' || type === 'team';

	return (
		<Inline space="space.100" alignBlock="center">
			<Box xcss={iconContainer}>
				{isTeamType && avatarSrc && fg('confluence_frontend_perms_exp_teams_as_principals') ? (
					<Avatar appearance="square" src={avatarSrc} />
				) : (
					<IconTile label="" size="32" appearance="gray" shape="circle" icon={PeopleGroupIcon} />
				)}
			</Box>
			<Stack xcss={principalDetailsStyle}>
				<Inline space="space.100" alignBlock="center">
					{name}
				</Inline>
				{/* Team subtext */}
				{isTeamType && fg('confluence_frontend_perms_exp_teams_as_principals') ? (
					<Inline space="space.050" alignBlock="center" testId="team-details-subtext">
						<Text size="small" color="color.text.subtlest">
							<TeamProfileCardWithTrigger
								cloudId={cloudId}
								userId={id || ''}
								teamAri={`ari:cloud:identity::team/${id}`}
								showFlag={undefined}
								additionalActions={[]}
							>
								{teamTypeName ?? formatMessage(i18n.teamText)}
							</TeamProfileCardWithTrigger>
						</Text>
						{isVerified && (
							<Tooltip
								content={formatMessage(i18n.verifiedTeamToolTip)}
								position="right"
								hideTooltipOnMouseDown
								ignoreTooltipPointerEvents
								delay={100}
							>
								<StatusVerifiedIcon label="" size="small" color={token('color.icon.accent.blue')} />
							</Tooltip>
						)}
						<Text size="small" color="color.text.subtlest">
							{formatMessage(i18n.membersCount, { count: memberCount })}
						</Text>
					</Inline>
				) : (
					// Group subtext
					<Inline space="space.050" alignBlock="center" testId="group-details-subtext">
						<Text size="small" color="color.text.subtlest">
							{formatMessage(
								fg('confluence_frontend_perms_exp_teams_as_principals')
									? i18n.adminGroupText
									: i18n.groupText,
							)}
						</Text>
						{fg('confluence_frontend_perms_exp_teams_as_principals') ? (
							<StatusVerifiedIcon label="" size="small" color={token('color.icon.accent.blue')} />
						) : null}
						<Text size="small" color="color.text.subtlest">
							{formatMessage(i18n.membersCount, { count: memberCount })}
						</Text>
					</Inline>
				)}
			</Stack>
		</Inline>
	);
};
